import moment from 'moment';
import { FC, useEffect, useMemo, useState } from 'react';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { formatStringToDateObj } from 'src/utils/helpers';
import { TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalFooter from 'src/view/components/Controls/Modal/ModalFooter/ModalFooter';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import FullDayStatusSwitcher from 'src/view/containers/Modals/WorkingHoursModal/components/FullDayStatusSwitcher/FullDayStatusSwitcher';
import MenuGroupPicker from 'src/view/containers/Modals/WorkingHoursModal/components/MenuGroupPicker/MenuGroupPicker';
import WorkingHoursOrderTypes from 'src/view/containers/Modals/WorkingHoursModal/components/WorkingHoursOrderTypes/WorkingHoursOrderTypes';
import WorkingHoursTimePicker from 'src/view/containers/Modals/WorkingHoursModal/components/WorkingHoursTimePicker/WorkingHoursTimePicker';
import { IWorkingHoursValidation, validateWorkingHours } from 'src/view/containers/Modals/WorkingHoursModal/helpers/validation';
import { WORKING_HOURS_ORDER_TYPE } from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/types/branchEdit';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import './style.scss';

const defaultStartTime = '02:00:00';
const defaultCloseTime = '14:00:00';

const WorkingHoursModal: FC = (): JSX.Element => {
  const modalParams = useMemo(
    () => ({
      weekday: modalStore.workingHoursModal!.weekday,
      id: modalStore.workingHoursModal?.id,
      startTime: formatStringToDateObj(modalStore.workingHoursModal?.startTime || defaultStartTime),
      closeTime: formatStringToDateObj(modalStore.workingHoursModal?.closeTime || defaultCloseTime),
      menuGroupId: modalStore.workingHoursModal?.menuGroupId,
      menuGroupName: modalStore.workingHoursModal?.menuGroupName,
      orderType: modalStore.workingHoursModal?.orderType,
      isFullDay: modalStore.workingHoursModal?.isFullDay,
      posIntRestriction: modalStore.workingHoursModal!.posIntRestriction,
      isOrderTypeDisabled: modalStore.workingHoursModal?.isOrderTypeDisabled,
      validationSlotsHours: modalStore.workingHoursModal!.validationSlotsHours,
      onSave: modalStore.workingHoursModal!.onSave,
      onDelete: modalStore.workingHoursModal!.onDelete,
      withoutMenuGroup: modalStore.workingHoursModal!.withoutMenuGroup || false,
      withoutOrderType: modalStore.workingHoursModal!.withoutOrderType || false,
    }),
    []
  );

  const [startTime, setStartTime] = useState<Date>(modalParams.startTime);
  const [closeTime, setCloseTime] = useState<Date>(modalParams.closeTime);
  const [isFullDay, setIsFullDay] = useState<boolean>(modalParams.isFullDay || false);

  const [menuGroupOption, setMenuGroupOption] = useState<IPickerOption | null>(() => {
    if (modalParams.menuGroupId === undefined || modalParams.menuGroupName === undefined) {
      return { value: 0, label: localeStore.t('default') };
    }
    return { value: modalParams.menuGroupId, label: modalParams.menuGroupName };
  });
  const [orderTypes, setOrderTypes] = useState<WORKING_HOURS_ORDER_TYPE[]>(() => {
    if (modalParams.orderType === undefined || modalParams.orderType === WORKING_HOURS_ORDER_TYPE.BOTH) {
      return [WORKING_HOURS_ORDER_TYPE.PICKUP, WORKING_HOURS_ORDER_TYPE.DELIVERY];
    }
    return [modalParams.orderType];
  });

  const [workingHoursValidation, setWorkingHoursValidation] = useState<IWorkingHoursValidation>({
    isValidStartTime: true,
    isValidCloseTime: true,
    isValidFullDay: true,
    isValidOrderType: true,
  });

  useEffect(() => {
    if (isFullDay) {
      setStartTime(formatStringToDateObj('00:00:00'));
      setCloseTime(formatStringToDateObj('23:59:59'));
    }
  }, [isFullDay]);

  const onSave = () => {
    const validation = validateWorkingHours(
      modalParams.id,
      modalParams.weekday,
      moment(startTime, 'HH:mm:ss'),
      moment(closeTime, 'HH:mm:ss'),
      isFullDay,
      orderTypes,
      modalParams.validationSlotsHours
    );

    setWorkingHoursValidation(validation);

    if (Object.values(validation).every(Boolean)) {
      modalParams.onSave(
        modalParams.weekday,
        modalParams.id,
        startTime,
        closeTime,
        Number(menuGroupOption?.value),
        menuGroupOption?.label,
        orderTypes.length > 1 ? WORKING_HOURS_ORDER_TYPE.BOTH : orderTypes[0]
      );
      modalStore.closeWorkingHoursModal();
    }
  };

  const onDelete = () => {
    modalParams.onDelete(modalParams.id);
    modalStore.closeWorkingHoursModal();
  };

  const onChangeStartTime = (time: Date) => setStartTime(time);

  const onChangeCloseTime = (time: Date) => setCloseTime(time);

  const onChangeFullDayStatus = () => setIsFullDay((currState) => !currState);

  const onChangeOrderType = (orderTypeID: WORKING_HOURS_ORDER_TYPE) => {
    setOrderTypes((selectedOrderTypes) => {
      const orderTypeIdx = selectedOrderTypes.indexOf(orderTypeID);

      if (~orderTypeIdx) {
        return selectedOrderTypes.filter((orderType) => orderType !== orderTypeID);
      } else {
        return [...selectedOrderTypes, orderTypeID];
      }
    });
  };

  const onChangeMenuGroupOption = (options: IPickerOption[]) => {
    setMenuGroupOption(options.length ? options[0] : null);
  };

  const timeOnly = modalParams.withoutMenuGroup && modalParams.withoutOrderType;

  return (
    <CustomModalComponent
      isOpen
      modalMaxWidth={timeOnly ? 582 : 612}
      className="h-fit-content"
      withCloseIcon
      onRequestClose={modalStore.closeWorkingHoursModal}
    >
      <ModalBackground>
        <ModalHeader title={localeStore.t(modalParams.weekday)} />

        <div className="d-flex flex-row gap-60 pt-6 px-8">
          <div className={`d-flex flex-column ${timeOnly ? 'w-100' : 'w-50'}`}>
            <div className={`d-flex ${timeOnly ? 'flex-row gap-30' : 'flex-column'}`}>
              <WorkingHoursTimePicker
                title="branch_opening_time_header_for_timeslot"
                time={startTime}
                disabled={isFullDay}
                posIntRestriction={modalParams.posIntRestriction}
                error={!workingHoursValidation.isValidStartTime}
                onChange={onChangeStartTime}
                className={`${timeOnly ? 'w-50' : ''}`}
              />
              <WorkingHoursTimePicker
                title="close"
                time={closeTime}
                disabled={isFullDay}
                posIntRestriction={modalParams.posIntRestriction}
                error={!workingHoursValidation.isValidCloseTime}
                className={`${timeOnly ? 'w-50' : 'mt-4'}`}
                onChange={onChangeCloseTime}
              />
            </div>

            <FullDayStatusSwitcher
              fullDay={isFullDay}
              posIntRestriction={modalParams.posIntRestriction}
              error={!workingHoursValidation.isValidFullDay}
              onChange={onChangeFullDayStatus}
            />
          </div>

          {timeOnly ? null : (
            <div className="d-flex flex-column w-50">
              {modalParams.withoutMenuGroup ? null : <MenuGroupPicker menuGroupOption={menuGroupOption} onSelect={onChangeMenuGroupOption} />}
              {modalParams.withoutOrderType ? null : (
                <WorkingHoursOrderTypes
                  selectedOrderTypes={orderTypes}
                  posIntRestriction={modalParams.posIntRestriction}
                  error={!workingHoursValidation.isValidOrderType}
                  onSelect={onChangeOrderType}
                  disabled={modalParams.isOrderTypeDisabled}
                />
              )}
            </div>
          )}
        </div>

        <ModalFooter className="mt-8 gap-20">
          <Button title={localeStore.t('apply_button')} onClick={onSave} size="large" />

          {/* show delete btn if slot is already created */}
          {modalParams.id !== undefined && (
            <IntegrationManager
              setting={modalParams.posIntRestriction.access}
              withTooltip
              tooltipOptions={{ show: modalParams.posIntRestriction.shouldDisable, minWidth: 355, position: TOOLTIP_POSITION.PORTAL }}
            >
              <Button title={localeStore.t('delete_timeslot_button')} onClick={onDelete} appearance="destructive-tertiary" size="large" />
            </IntegrationManager>
          )}
        </ModalFooter>
      </ModalBackground>
    </CustomModalComponent>
  );
};

export default WorkingHoursModal;
